/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
// import config from "../config/updateProfile.config.json";

import axios from "axios";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
// import { UPDATE_MY_PROFILE } from "../graphql";
import { VApplication } from "../../../application";
import { CREATE_DUMMY_HURRICANE } from "../graphql";
import { v4 as uuidv4 } from "uuid";
import uuid from "react-uuid";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ITriggerCycloneMethods extends IPluginMethodMap {
  getTriggerCyclone(args: any): Promise<any>;
}

export interface TriggerCycloneProps {
  event: {
    eventId: string;
    exposure: string;
    stormName: string;
    vmin: number;
    vmax: number;
    date: Date;
    country: string;
    category: string;
    status: string;
    reason: string;
    payoutEstimate?: string;
  };
  // payoutEstimateRaw: string;
  // blockchainTxnHash: string;
}

export class TriggerCycloneService implements IAgentPlugin {
  readonly methods: ITriggerCycloneMethods;

  constructor() {
    this.methods = {
      getTriggerCyclone: this.getTriggerCyclone.bind(this),
    };
  }
  

  async getTriggerCyclone(args: TriggerCycloneProps) {
    console.log("Data sent to mutation:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        // Define vmin and vmax based on category
        let vmin, vmax;
      
        switch (args.event?.category) {
          case "tropical_storm":
            vmin = 18;
            vmax = getRandomVmax(18, 33); // vmax between 18 and 33 (but not 33)
            break;
          case "category_1":
            vmin = 33;
            vmax = getRandomVmax(33, 43); // vmax between 33 and 43 (but not 43)
            break;
          case "category_2":
            vmin = 43;
            vmax = getRandomVmax(43, 50); // vmax between 43 and 50 (but not 50)
            break;
          case "category_3":
            vmin = 50;
            vmax = getRandomVmax(50, 58); // vmax between 50 and 58 (but not 58)
            break;
          case "category_4":
            vmin = 58;
            vmax = getRandomVmax(58, 70); // vmax between 58 and 70 (but not 70)
            break;
          case "category_5":
            vmin = 70;
            vmax = getRandomVmax(70, 90); // vmax between 70 and 90 (but not 90)
            break;
          default:
            vmin = null;
            vmax = null;
            console.log("Unknown category");
            break;
        }
      
        const res = await client.mutate({
          mutation: CREATE_DUMMY_HURRICANE,
          variables: {
            input: {
              eventId: uuidv4(),
              exposure: args.event?.exposure,
              stormName: args.event?.stormName,
              vmin: vmin?.toString(),
              vmax: vmax?.toString(),
              date: new Date(),
              country: "dm",
              category: args.event?.category,
              status: "ACTIVE",
            },
          },
          fetchPolicy: "no-cache",
        });
      
        if (res?.data) {
          console.log("GraphQL response:", res?.data);
          return {
            data: res?.data,
          };
        }
      }
    } catch (err: any) {
      console.error("Error occurred:", err);
      throw err;
    }

    return true;
  }
}

function getRandomVmax(vmin: number, max: number): number {
  // Generate a random decimal number between vmin and (max - 0.01)
  const randomValue = Math.random() * (max - vmin - 0.01) + vmin;
  
  // Round to 2 decimal places and return as a number
  return Math.round(randomValue * 100) / 100;
}
